import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import adminService from '../../../../services/adminService';
import type { User } from '../../../../types/user';
import Loader from '../../../../components/common/Loader';
import { showToast } from '../../../../utils/showToast';
import { promoteMentor, demoteMentor } from '../../../../services/mentor/dashboardService';
import { UserCheck, UserX, UserPlus, Search, Users } from 'lucide-react';

// Step 1: Lazy load the Modal component for better performance
const CreateMentorModal = lazy(() => import('./CreateMentorModal'));

const ManageMentors: React.FC = () => {
    const { user, isLoading: isAuthLoading } = useAuth();
    const [allMentors, setAllMentors] = useState<User[]>([]);
    const [filteredMentors, setFilteredMentors] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchMentors = async () => {
        setIsLoading(true);
        try {
            const data = await adminService.getAllMentors();
            setAllMentors(data);
            setFilteredMentors(data);
        } catch (err: any) {
            showToast(err.message, { type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isAuthLoading && user?.isAdmin) {
            fetchMentors();
        }
    }, [isAuthLoading, user]);
    
    useEffect(() => {
        const lowercasedFilter = searchTerm.toLowerCase();
        const filteredData = allMentors.filter(mentor =>
            mentor.name.toLowerCase().includes(lowercasedFilter) ||
            mentor.email.toLowerCase().includes(lowercasedFilter)
        );
        setFilteredMentors(filteredData);
    }, [searchTerm, allMentors]);

    const handlePromoteMentor = async (mentor: User) => {
        if (!window.confirm(`Are you sure you want to promote ${mentor.name} to Admin?`)) return;
        setIsProcessing(mentor._id);
        try {
            await promoteMentor(mentor._id);
            showToast(`${mentor.name} promoted to Admin.`, { type: 'success' });
            setAllMentors(prev => prev.map(m => m._id === mentor._id ? { ...m, isAdmin: true } : m));
        } catch (err: any) {
            showToast(err.message, { type: 'error' });
        } finally {
            setIsProcessing(null);
        }
    };

    const handleDemoteMentor = async (mentor: User) => {
        if (!window.confirm(`Are you sure you want to demote ${mentor.name} from Admin?`)) return;
        setIsProcessing(mentor._id);
        try {
            await demoteMentor(mentor._id);
            showToast(`${mentor.name} demoted from Admin.`, { type: 'success' });
            setAllMentors(prev => prev.map(m => m._id === mentor._id ? { ...m, isAdmin: false } : m));
        } catch (err: any) {
            showToast(err.message, { type: 'error' });
        } finally {
            setIsProcessing(null);
        }
    };

    if (isAuthLoading || isLoading) {
        return <Loader />;
    }

    const renderActionButtons = (mentor: User) => (
        <div className="flex justify-end items-center">
            {!mentor.isAdmin ? (
                <button 
                    onClick={() => handlePromoteMentor(mentor)} 
                    disabled={!!isProcessing} 
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md disabled:opacity-50 transition"
                >
                    <UserCheck className="h-4 w-4" /> Promote
                </button>
            ) : (
                <button 
                    onClick={() => handleDemoteMentor(mentor)} 
                    disabled={!!isProcessing} 
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md disabled:opacity-50 transition"
                >
                    <UserX className="h-4 w-4" /> Demote
                </button>
            )}
        </div>
    );

    return (
        <div className="space-y-6">
            <header className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">Mentor Management</h1>
                        <p className="text-gray-500 mt-1">Add, view, and manage mentor permissions.</p>
                    </div>
                    <div className="flex flex-col sm:flex-row w-full md:w-auto gap-2">
                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-11 pr-4 py-2.5 border border-gray-300 rounded-full bg-gray-50 focus:ring-2 focus:ring-blue-500 transition"
                            />
                        </div>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2.5 rounded-full text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-2"
                        >
                            <UserPlus className="w-4 h-4" />
                            Add Mentor
                        </button>
                    </div>
                </div>
            </header>

            {filteredMentors.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl shadow-sm border">
                    <Users className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-lg font-medium text-gray-900">No Mentors Found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {searchTerm ? 'No mentors match your search.' : 'Click "Add Mentor" to get started.'}
                    </p>
                </div>
            ) : (
                <>
                    <div className="hidden md:block bg-white rounded-2xl shadow-sm border overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Admin Status</th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredMentors.map(mentor => (
                                    <tr key={mentor._id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <p className="font-medium text-gray-900">{mentor.name}</p>
                                            <p className="text-sm text-gray-500">{mentor.email}</p>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${mentor.isAdmin ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                {mentor.isAdmin ? 'Admin' : 'Mentor'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            {renderActionButtons(mentor)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="md:hidden space-y-4">
                        {filteredMentors.map(mentor => (
                            <div key={mentor._id} className="bg-white p-4 rounded-2xl shadow-sm border space-y-4">
                                <div>
                                    <p className="font-bold text-gray-900">{mentor.name}</p>
                                    <p className="text-sm text-gray-500">{mentor.email}</p>
                                </div>
                                <div className="flex items-center justify-between text-sm border-t pt-4">
                                    <div>
                                        <p className="text-gray-600 font-medium mb-1">Role</p>
                                        <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${mentor.isAdmin ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                            {mentor.isAdmin ? 'Admin' : 'Mentor'}
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        {renderActionButtons(mentor)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            <Suspense fallback={null}>
                <CreateMentorModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSuccess={() => {
                        fetchMentors();
                        setIsModalOpen(false);
                    }}
                />
            </Suspense>
        </div>
    );
};

export default ManageMentors;